/* D48-d -- PWA update prompt.
 *
 * PROBLEM: Every deploy leaves at least one client with the previous
 * bundle cached by the service worker. Twice now that stale bundle
 * has been mis-reported as "the preview is broken" (D44 NotifTemplates
 * post-fix cache + D48-c same-pattern recurrence). The existing D22
 * registration logic in `src/index.js` handles the FIRST fresh visit
 * per bump via `sw-version.json` polling -- but it doesn't tell an
 * already-loaded tab that a new version is waiting.
 *
 * SOLUTION: When the browser's SW machinery detects a new SW installing
 * (browser fires `updatefound` on the registration), watch the new
 * SW's state transitions. Once it reaches `installed` state WHILE
 * another SW is still active (`navigator.serviceWorker.controller` is
 * non-null), we know a new version is queued behind the current one.
 * At that point we surface a neutral toast: "A new version is
 * available -- refresh to update" with a Refresh button that:
 *
 *   1. Posts `{type: "sw:skipWaiting"}` to the waiting SW (the sw.js
 *      handler in `public/sw.js` calls `self.skipWaiting()`).
 *   2. Listens once for `controllerchange` on
 *      `navigator.serviceWorker` -- fires as soon as the new SW
 *      activates and takes control.
 *   3. Reloads the page inside the controllerchange handler.
 *
 * PROACTIVE CHECKS: To catch versions that ship while the tab is
 * open, we call `registration.update()` on tab-focus (visibilitychange
 * -> visible) AND on a 30-minute interval. Both are cheap and only
 * fetch the SW file itself + its imports; the browser dedupes.
 *
 * TOAST STYLE: uses `sonner` `toast.info` (Sky accent, matches app
 * design tokens). Sticky (duration: Infinity) until the user clicks
 * Refresh or Dismiss so a quick tab-switch doesn't lose the prompt.
 */
import { toast } from "sonner";

let _wired = false;
let _promptedForRegistration = null;   // last reg we've already shown a toast for

/** Idempotent boot -- safe to call multiple times. */
export function initSwUpdatePrompt() {
  if (_wired) return;
  _wired = true;
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  // When any waiting SW activates and takes control, reload once.
  let _reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (_reloading) return;
    _reloading = true;
    // Small delay so the toast dismiss animation can complete + the
    // new SW finishes claiming clients.
    setTimeout(() => { try { window.location.reload(); } catch (_e) { /* ignore */ } }, 150);
  });

  // Attach the updatefound listener to whichever registration exists
  // (or will exist) for our SW. The registration is created inside
  // `src/index.js` after the version manifest fetch; we poll briefly
  // and hook in as soon as it's available.
  const attach = async () => {
    let reg = null;
    for (let i = 0; i < 30; i += 1) {   // up to ~15s of polling
      reg = await navigator.serviceWorker.getRegistration();
      if (reg) break;
      await new Promise((r) => setTimeout(r, 500));
    }
    if (!reg) return;   // no SW ever registered -- give up quietly

    // If we already have a waiting SW at attach-time, surface immediately.
    if (reg.waiting && navigator.serviceWorker.controller) {
      _promptRefresh(reg);
    }

    reg.addEventListener("updatefound", () => {
      const nw = reg.installing;
      if (!nw) return;
      nw.addEventListener("statechange", () => {
        // `installed` while a controller exists = a NEW version is
        // waiting behind the current one. First-install (no existing
        // controller) is a cold boot; don't prompt in that case.
        if (nw.state === "installed" && navigator.serviceWorker.controller) {
          _promptRefresh(reg);
        }
      });
    });

    // Proactive checks so an already-open tab notices new versions.
    const UPDATE_INTERVAL_MS = 30 * 60 * 1000;      // 30 minutes
    setInterval(() => { reg.update().catch(() => null); }, UPDATE_INTERVAL_MS);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        reg.update().catch(() => null);
      }
    });
  };
  attach();
}

function _promptRefresh(reg) {
  // Dedup -- one toast per SW registration lifetime.
  if (_promptedForRegistration === reg) return;
  _promptedForRegistration = reg;

  toast.info("A new version is available", {
    id: "sw-update-available",
    description: "Refresh to load the latest Sharda Energy OS build.",
    duration: Infinity,
    action: {
      label: "Refresh",
      onClick: () => _activateWaiting(reg),
    },
    cancel: {
      label: "Later",
      onClick: () => { /* auto-dismiss by sonner */ },
    },
  });
}

function _activateWaiting(reg) {
  const waiting = reg.waiting;
  if (!waiting) {
    // Registration lost its waiting worker between prompt + click;
    // fall back to a plain reload (browser will fetch the new SW
    // during the next boot).
    try { window.location.reload(); } catch (_e) { /* ignore */ }
    return;
  }
  try {
    waiting.postMessage({ type: "sw:skipWaiting" });
    // The `controllerchange` listener (installed in initSwUpdatePrompt)
    // will fire once the new SW claims the client and reload.
  } catch (_e) {
    // If postMessage throws (rare), hard-reload immediately as a
    // safety net -- the version-pin check in `index.js` will pick up
    // the new bundle.
    try { window.location.reload(); } catch (_e2) { /* ignore */ }
  }
}
